import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import {
  api,
  authMe,
  authLogin,
  authSignup,
  authLogout,
  onAuthError
} from "@/lib/api";

/**
 * Auth states:
 *   user === undefined  → still resolving (first /me call in flight)
 *   user === null       → not authenticated
 *   user === {...}      → authenticated
 */
const AuthContext = createContext({
  user: undefined,
  login: async () => {},
  signup: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

const TOAST_DUP_KEY = Symbol("aureus-auth-toast");

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [subscription, setSubscription] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const lastToastAt = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const data = await authMe();
      setUser(data.user);
      setSubscription(data.subscription || null);
      return data.user;
    } catch (e) {
      setUser(null);
      setSubscription(null);
      return null;
    }
  }, []);

  // First mount → check existing cookie
  useEffect(() => {

      const token = localStorage.getItem("token");

      if (token) {
          api.defaults.headers.common.Authorization =
              `Bearer ${token}`;
      }

      refresh();

  }, [refresh]);

  // Listen for 401/403 from anywhere in the app and react to specific codes
  useEffect(() => {
    return onAuthError(({ status, code }) => {
      if (status !== 401) return; // ignore 403s here (e.g., admin gated routes)
      // SESSION_REVOKED = the critical "another device logged in" case
      const isRevoked = code === "SESSION_REVOKED";
      const isExpired = code === "TOKEN_EXPIRED";
      if (!isRevoked && !isExpired && code !== "NOT_AUTHENTICATED") return;
      // Avoid hammering toast on every retry
      const now = Date.now();
      if (now - lastToastAt.current < 3000) return;
      lastToastAt.current = now;
      // Already on the login page? Just silently keep user=null.
      if (location.pathname.startsWith("/login") || location.pathname.startsWith("/signup")) {
        setUser(null);
        return;
      }
      setUser(null);
      if (isRevoked) {
        toast({
          title: "Signed in from another device",
          description: "Only one active session is allowed. Please log in again.",
          variant: "destructive",
        });
      } else if (isExpired) {
        toast({
          title: "Session expired",
          description: "Please log in again to continue.",
        });
      }
      navigate("/login", { replace: true, state: { from: location.pathname } });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toast/navigate identity stable enough for our usage
  }, [location.pathname]);

  const login = useCallback(async (email, password) => {

      const data = await authLogin({
          email,
          password
      });

      localStorage.setItem("token", data.token);

      localStorage.setItem(
          "user",
          JSON.stringify(data.user)
      );

      api.defaults.headers.common.Authorization =
          `Bearer ${data.token}`;

      setUser(data.user);

      return data.user;

  }, []);

  const signup = useCallback(async (body) => {
    return authSignup(body); // does NOT log the user in — they're PENDING
  }, []);

  const logout = useCallback(async () => {

    try {
        await authLogout();
    } catch (_) {}

    localStorage.removeItem("token");

    localStorage.removeItem("user");

    delete api.defaults.headers.common.Authorization;

    setUser(null);

    navigate("/login", {
        replace: true
    });

  }, [navigate]);

  return (
    <AuthContext.Provider value={{ user, subscription, login, signup, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// Used by AuthContext to avoid repeated toast spam
export { TOAST_DUP_KEY };
